import { Form, Formik } from "formik";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useTranslation, withTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Hero from "../common/Hero/Hero";
import Loader from "../common/Loader/Loader";
import "./CaseGenerator.scss";
import CaseSummary from "./components/CaseSummary/CaseSummary";
import { Cases } from "./definitions/Cases";

import { ApolloError } from "apollo-client";
import { client } from "../../App";
import { CaseMutationType, CaseStepperType } from "../../helpers/enums";
import {
  getPrivacyLink,
  standardizeGraphQLError,
  TranslatedText,
} from "../../helpers/helpers";
import {
  getEvictionForMutation,
  SEND_COMPLEX_CASE,
  SEND_SIMPLE_CASE,
} from "../../helpers/mutations";
import { CaseStep } from "./components/CaseStep/CaseStep";
import { CaseStepper } from "./components/CaseStepper/CaseStepper";
import CaseStepperSimple from "./components/CaseStepperSimple/CaseStepperSimple";
import RouteLeavingGuard from "./RouteLeavingGuard";
import { ProviderMapping } from "./service/ProviderMapping";

import { CaseErrorBoundary } from "./CaseErrorBoundary";

import { useDispatch } from "react-redux";
import useAmplifyAuth from "../../helpers/useAmplifyAuth";
import { setCurrentStateAction } from "../../reducers/caseGeneratorCurrentStep";
import Modal from "../common/Modal/Modal";
import getInterfaceTypes from "./types";

interface CaseGeneratorProps {
  type: string;
  initialValues: any;
  title: boolean;
  embed: boolean;
  handleBack: () => {};
  handleClose: () => {};
  currentStep: number;
  t: any;
  block: boolean;
  blockText: string;
  blockIfNoContracts: boolean;
  signDigital: boolean;
  successModalCustom: any | null;
  excludeSteps: number[];
  webviewPopup: any | null;
  contractsState: any[];
  webviewRedirectPopup: any;
}

type InterfaceFieldType = 
  | 'input' | 'radio' | 'text' | 'checkbox' | 'button' | 'password'
  | 'date' | 'datepicker'
  | 'select' | 'select-search' | 'dynamic-select' | 'stateSelect' 
  | 'active-contract-select' | 'searchWithSelect' | 'selectWithRemoteOptions'
  | 'captcha' | 'file' | 's3File' | 'switch-radio' | 'google-address' 
  | 'egon-address' | 'google-address-fix' | 'suggestions' | 'iban';

  interface CaseOutput {
  model: any;
  validationSchema: any;
  initialValues: any;
  layout: any;
  formDetails: any;
  mutation: MutationData;
  translatedInverseMapping?: any;
  summaryLayout?: any;
  translatedMapping?: any;
  trackingEvent?: any;
  trackingEventKo?: any;
}
interface MutationData {
  mutation: any;       
  type: string;         
  refetchQueries?: any[]; 
}
interface MappingData {
  draft: boolean;
  type: string;
  draftAsJson: string;
  caseId?: string | number;
}

export const CaseFormField = (props: any): any => {
  const { type, ...otherProps }: any = props;
  const r: any = getInterfaceTypes(otherProps)[type as InterfaceFieldType];

  if (!r) {
    console.log(props);
    return (
      <div>
        No field interface defined for {type} - Field: {props.field.name}
      </div>
    );
  }

  return r;
};

const CaseGenerator = (props: CaseGeneratorProps) => {
  const [buttonVolturaIsClick, setButtonVolturaIsClick] = useState(false);
  const [ctaPrivacyLabel, setCtaPrivacyLabel] = useState("");
  const privacyPdfPageLink = getPrivacyLink(props.type);
  const [counter, setCounter] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const {
    model,
    validationSchema,
    initialValues,
    layout,
    formDetails,
    mutation,
    translatedInverseMapping,
    summaryLayout,
    translatedMapping,
  } = Cases[props.type as keyof typeof Cases](
    props.excludeSteps,
    counter,
    props.signDigital,
    props.webviewPopup,
    props.webviewRedirectPopup,
    buttonVolturaIsClick,
    privacyPdfPageLink,
    ctaPrivacyLabel,
    activeStep,
    props.contractsState
  ) as CaseOutput;
  const {
    title: shouldShowHero = true,
    embed = false,
    blockIfNoContracts = false,
  } = props;
  const { signDigital, successModalCustom } = props;
  const { currentStep = null } = props;
  const steps = layout.map((item: any) => item.title);
  const { formId, formField } = model;
  const [flowFinished, setFlowFinished] = useState(false);
  const [errorSend, setErrorSend] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pdfLink, setPdfLink] = useState(null);
  const [caseNumber, setCaseNumber] = useState(null);
  const currentValidationSchema = validationSchema[activeStep];
  const dispatch = useDispatch();
  const {
    helpText,
    backTo = "/",
    successText,
    belowText,
    aboveImg,
    incompleteActivation = false,
    formOnly = false,
    autoOpenHelpModal = false,
    removeTagDescriptionInHero = false,
    blockText = "",
    submitButton,
  } = formDetails;
  const [isSummary, setIsSummary] = useState(false);
  const history = useHistory();
  const [summaryModalVisible, setSummaryModalVisible] = useState(false);
  const [summaryEditingLayout, setSummaryEditingLayout] = useState(null);
  const [summaryInitialValues, setSummaryInitialValues] = useState(null);
  const [summaryValidationSchema, setSummaryValidationSchema] = useState(null);
  const [summaryDataToUpdate, setSummaryDataToUpdate] = useState(null);
  const [deactiveIncompleteControl, setDeactiveIncompleteControl] =
    useState(false);
  const [show, setShow] = useState(false);
  const [showErrorGql, setShowErrorGql] = useState(false);
  let dirtyFlag = false;

  useEffect(() => {
    if (summaryLayout && currentStep == null) {
      setActiveStep(steps.length - 1);
      setIsSummary(true);
    }
    if (!mutation) {
      console.warn("No mutation set for case ", props.type);
    }
    if (currentStep != null) {
      setActiveStep(currentStep);
      setIsSummary(false);
    }
    setCounter(counter + 1);
    // eslint-disable-next-line
  }, [currentStep, mutation, props.type]);
  const { refreshToken } = useAmplifyAuth();
  const { t } = props;
  const { backToText = t("common.back.home") } = formDetails;
  const propsInitialValues = props.initialValues || {};
  const currentLayout = layout[activeStep];
  let { beforeSubmit } = currentLayout;
  const isLastStep = activeStep === steps.length - 1;
  const PdfFragment = (props: any) => {
    const { link, caseNumber } = props;
    return (
      <React.Fragment>
        <div className="content">
          {props.content}
          {caseNumber && (
            <p className="case-number">
              <TranslatedText i18nKey="cases.generic.caseNumber" /> :{" "}
              <span className="bold">{caseNumber}</span>
            </p>
          )}
          <p>
            <TranslatedText i18nKey="cases.complex.success.pdf_part_2" />
            <span
              className={"link_3 mt-2"}
              onClick={() => {
                window.open(link);
              }}
            >
              {" "}
              <TranslatedText i18nKey="cases.complex.success.pdf_action_link" />
            </span>
          </p>
        </div>
      </React.Fragment>
    );
  };

  const LabelCtaAction = () => {
    const labelCtaAction =
      layout.length === 1 || (isLastStep && props.handleClose)
        ? t("cases.buttons.send")
        : isLastStep && submitButton === undefined
        ? t("cases.buttons.finish")
        : isLastStep && submitButton !== ""
        ? t(submitButton)
        : t("cases.buttons.continue");

    const labelCtaPrivacy =
      layout.length === 1 || ((isLastStep && props.handleClose) && !isSummary)
        ? t("cases.buttons.send")
        : isLastStep && submitButton === undefined && !isSummary
        ? t("cases.buttons.finish")
        : isLastStep && submitButton !== "" && !isSummary
        ? t(submitButton)
        : t("cases.buttons.continue");

    useEffect(() => {
      setCtaPrivacyLabel(labelCtaPrivacy);
    });

    return labelCtaAction;
  };

  const refreshTokenHandler = () => {
    return refreshToken();
  };

  const SuccessModalCaseGenerator = (props: any) => {
    const { t } = useTranslation();
    const { caseNumber, errorMessage, isError } = props;
    if (props.isOpen) {
      if (signDigital) {
        return successModalCustom({
          caseNumber,
          errorMessage,
          isError,
          t,
          embed,
        });
      }
      return props.isOpen && props.webviewPopup ? (
        props.webviewPopup(
          null,
          isError
            ? errorMessage
              ? t(errorMessage)
              : t("cases.generic_error")
            : t("cases.request_info.success_text"),
          t("common.close")
        )
      ) : props.isOpen ? (
        <Modal
          icon={
            !props.isError
              ? "gc-icon-functional-check-2-col"
              : "gc-icon-functional-attention-2-col"
          }
          show={props.isOpen}
          descriptionClass={"success-modal-text paragraph-main-fs8"}
          content={
            props.isError ? (
              <p>
                {props.errorMessage
                  ? t(props.errorMessage)
                  : t("cases.generic_error")}
              </p>
            ) : props.pdfLink ? (
              <PdfFragment
                content={props.content}
                link={props.pdfLink}
                caseNumber={props.caseNumber}
              />
            ) : (
              <div className="content">
                {props.content}
                {props.caseNumber && (
                  <p>
                    <TranslatedText i18nKey="cases.generic.caseNumber" /> :{" "}
                    <span className="bold">{props.caseNumber}</span>
                  </p>
                )}
              </div>
            )
          }
          type={embed ? "a" : "link"}
          onFirstClick={
            embed
              ? (e: React.SyntheticEvent) => {
                  e.preventDefault();
                  window.location.reload();
                }
              : null
          }
          to={"/"}
          linkTitle={embed ? t("common.close") : t("common.goToDashboard")}
        />
      ) : null;
    } else {
      return null;
    }
  };

  const SummarySectionModal = (props: any) => {
    if (currentStep) {
      dispatch(setCurrentStateAction(currentStep));
    }
    const { setSummaryModalVisible, fields, editingLayout } = props;

    function update(values: any) {
      console.log("updating -- ", values);
      setSummaryModalVisible(false);
      document?.querySelector("body")?.classList.remove("modal-open");
      setSummaryDataToUpdate(values);
    }

    const getContextualInitialValues = () => {
      let layoutFields = editingLayout.fields.map(
        (item: any) => item.fieldName
      );
      if (editingLayout.fieldcondition) {
        layoutFields.push(...editingLayout.fieldcondition);
      }
      return {
        ..._.pick(fields, layoutFields),
      };
    };

    const getContextualFields = () => {
      // const layoutFields = editingLayout.fields.map(item => item.fieldName);
      return {
        ...formField,
      };
    };

    return props.isOpen ? (
      <Modal
        show={props.isOpen}
        onClose={() => setSummaryModalVisible(false)}
        noIcon={true}
        isWithForm={true}
        children={
          <Formik
            initialValues={getContextualInitialValues()}
            validationSchema={summaryValidationSchema}
            onSubmit={update}
          >
            {({ isSubmitting, errors, dirty, values }) => (
              <Form
                id={formId}
                noValidate
                autoComplete={"off"}
                className="case-generated-form"
              >
                <CaseStep
                  fields={getContextualFields()}
                  errors={errors}
                  layout={editingLayout}
                />
                <div className={"d-flex"}>
                  <Button className={"btn btn-primary"} type="submit">
                    {t("button.confirm")}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        }
      />
    ) : null;
  };

  const getCaseMutation = (type: string, data: any) => {
    const {
      mutation,
      type: mutationType,
      refetchQueries,
    } = (Cases[props.type as keyof typeof Cases]() as CaseOutput).mutation;
    const { formId } = Cases[props.type as keyof typeof Cases]().model;
    switch (mutationType) {
      case CaseMutationType.Simple:
        return client().mutate({
          mutation: SEND_SIMPLE_CASE,
          variables: { req: ProviderMapping.simpleCase(data, formId) },
          update: (cache) => {
            const fieldsToEvict = getEvictionForMutation("simpleCase");
            fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
            cache.gc();
          },
        });
      case CaseMutationType.Complex:
        // console.log(ProviderMapping.complexCase(data, formId));
        return client().mutate({
          mutation: SEND_COMPLEX_CASE,
          variables: {
            req: ProviderMapping.complexCase(
              data,
              formId,
              props.excludeSteps?.length > 0,
              []
            ),
          },
          update: (cache) => {
            const fieldsToEvict = getEvictionForMutation("complexCase");
            fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
            cache.gc();
          },
        });
      case CaseMutationType.Custom: {
        return client().mutate({
          mutation: mutation,
          variables: {
            req: ProviderMapping.complexCase(
              data,
              formId,
              props.excludeSteps?.length > 0,
              props.contractsState ? props.contractsState : []
            ),
          },
          //refetchQueries: refetchQueries,
          update: (cache) => {
            refetchQueries?.forEach((field: any) =>
              cache.evict({ fieldName: field })
            );
            cache.gc();
          },
        });
      }
      default:
        return client().mutate({
          mutation: SEND_SIMPLE_CASE,
          variables: { req: ProviderMapping.simpleCase(data, formId) },
          update: (cache) => {
            const fieldsToEvict = getEvictionForMutation("simpleCase");
            fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
            cache.gc();
          },
        });
    }
  };

  const incompleteMutation = (data: any) => {
    const { formId } = Cases[props.type as keyof typeof Cases]().model;
    const { caseId = null } = data;
    const fullData = { ...data, currentStep: activeStep };
    let mapping: MappingData = {
      draft: true,
      type: formId,
      draftAsJson: JSON.stringify(fullData),
    };
    if (caseId) {
      mapping = { ...mapping, caseId };
    }
    console.log(mapping, "incomplete mutations");
    return client().mutate({
      mutation: SEND_COMPLEX_CASE,
      variables: { req: mapping },
      update: (cache) => {
        const fieldsToEvict = getEvictionForMutation("complexCase");
        fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
        cache.gc();
      },
    });
  };

  function sleep(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function remotePromise(values: any) {
    await sleep(1000);
    return new Promise((resolve, reject) => {
      try {
        resolve(values);
      } catch (err) {
        reject(err);
      }
    });
  }

  const submitCase = async (values: any, actions: any) => {
    if (mutation && (dirtyFlag || currentStep != null)) {
      getCaseMutation(mutation.type, values)
        .then((response: any) => {
          console.log("RESPONSE", response);
          refreshTokenHandler();
          const { trackingEvent = null } = (Cases[props.type as keyof typeof Cases]() as CaseOutput);
          const {
            data: { complexCase, simpleCase },
          } = response;

          let redirectToUrl = null;
          if (trackingEvent) {
            trackingEvent();
          }
          if (complexCase) {
            if ("details" in complexCase) {
              const { details } = complexCase;
              const { path, caseNumber } = details;
              setCaseNumber(caseNumber);
              if (path && !signDigital) {
                setPdfLink(path);
                const link = document.createElement("a");
                link.href = path;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            }
          }
          if (simpleCase && "details" in simpleCase) {
            const { details } = simpleCase;
            const { caseNumber, paymentUrl } = details;
            redirectToUrl = paymentUrl;
            setCaseNumber(caseNumber);
          }
          if (redirectToUrl) {
            window.location.href = redirectToUrl;
          } else {
            actions.setSubmitting(false);
            setErrorSend(false);
            setFlowFinished(true);
            if (props.handleClose) {
              props.handleClose();
            }
          }
        })
        .catch((error: ApolloError) => {
          let errorValidation = false;
          console.log("ERROR:", error);
          const { trackingEventKo = null } = (Cases[props.type as keyof typeof Cases]() as CaseOutput);
          if (trackingEventKo) {
            trackingEventKo();
          }
          if ("graphQLErrors" in error) {
            console.log(error.graphQLErrors, "errors");
            if (error.graphQLErrors.length <= 0) {
              setErrorSend(true);
              setFlowFinished(true);
              return actions.setSubmitting(false);
            }
            const { extensions } = error.graphQLErrors[0];
            const { details }: any = extensions;
            const { validations } = details;
            if (translatedInverseMapping) {
              if (!validations) {
                const extractErr = standardizeGraphQLError(error);
                console.log(extractErr, "errorexta");
                if ("type" in extractErr) {
                  setErrorMessage(extractErr.error);
                }
                setErrorSend(true);
                setFlowFinished(true);
                return actions.setSubmitting(false);
              }
              const fieldErrors = translatedInverseMapping(
                validations,
                values,
                props.excludeSteps?.length > 0 ? true : false
              );
              console.log("Field Errors are: ", fieldErrors);
              actions.setErrors(fieldErrors);
              errorValidation = true;
              setShowErrorGql(true);
            } else {
              console.log(
                `Inverse mapping not defined for case [${props.type}]`
              );
            }
          }
          if (!errorValidation) {
            setErrorSend(true);
            setFlowFinished(true);
          }
          actions.setSubmitting(false);
        });
    } else if (!dirtyFlag) {
      history.push("/");
    } else {
      console.log("Warn: Skipping mutation");
      await sleep(1000);
      remotePromise(values)
        .then(() => {
          actions.setSubmitting(false);
          setFlowFinished(true);
          if (props.handleClose) {
            props.handleClose();
          }
        })
        .catch(() => {
          actions.setSubmitting(false);
        });
    }
  };

  function handleSubmit(values: any, actions: any) {
    if (isLastStep && (isSummary || !formDetails.summary)) {
      if (beforeSubmit) {
        beforeSubmit(values, actions)
          .then(() => {
            setDeactiveIncompleteControl(true);
            submitCase(values, actions);
          })
          .catch(() => {
            return actions.setSubmitting(false);
          });
      } else {
        setDeactiveIncompleteControl(true);
        submitCase(values, actions);
        console.log("I was called");
      }

      // setIsSummary(true);
    } else if (isLastStep && formDetails.summary) {
      // submitCase(values, actions);
      setIsSummary(true);
      actions.setSubmitting(false);
    } else {
      if (!beforeSubmit) {
        beforeSubmit = () =>
          new Promise<void>((resolve) => {
            actions.setSubmitting(false);
            resolve();
          });
      }
      beforeSubmit(values, actions, buttonVolturaIsClick)
        .then(() => {
          console.log("Running before submit");
          setActiveStep(activeStep + 1);
          setCounter(counter + 1);
          actions.setTouched({});
          actions.setSubmitting(false);
          if (
            model.isVolturaSubentroCase &&
            activeStep === 0 &&
            buttonVolturaIsClick
          ) {
            setShow(true);
          }
        })
        .catch((error: any) => {
          console.log("Blocked by beforeSubmit", error);
          actions.setSubmitting(false);
        });
    }
  }

  function handleBack() {
    if (isSummary) {
      setIsSummary(false);
    } else {
      setActiveStep(activeStep - 1);
    }
  }
  function handleTextButton(buttonVolturaIsClick: any) {
    if (buttonVolturaIsClick) {
      return t("cases.buttons.contract.data");
    } else {
      return t("cases.buttons.meter.serial.number");
    }
  }
  const blockPopUp = blockIfNoContracts || props.block;
  return (
    <CaseErrorBoundary>
      <div
        className={`CaseGenerator ${isSummary ? "CaseSummaryContainer" : ""} ${
          formDetails.stepper === CaseStepperType.None && "stepper-less"
        }`}
        data-testid={"CaseGenerator"}
      >
        {!formOnly && shouldShowHero && (
          <Hero
            title={formDetails.title}
            description={formDetails.description}
            helpText={blockPopUp ? blockText || props.blockText : helpText}
            backTo={blockPopUp ? backTo : formDetails.backTo || "/"}
            backToText={
              blockPopUp
                ? backToText
                : formDetails.backToText || t("common.back.home")
            }
            autoOpen={blockIfNoContracts || autoOpenHelpModal}
            removeTagDescriptionP={removeTagDescriptionInHero}
            block={blockPopUp}
          />
        )}
        <Container></Container>

        <SuccessModalCaseGenerator
          content={successText}
          isOpen={flowFinished}
          to={"/"}
          isError={errorSend}
          errorMessage={errorMessage}
          pdfLink={pdfLink}
          caseNumber={caseNumber}
          webviewPopup={props.webviewPopup}
        />
        <Modal
          show={showErrorGql}
          onClose={() => setShowErrorGql(false)}
          onFirstClick={() => setShowErrorGql(false)}
          type={"button"}
          linkTitle={t("common.close")}
          description={t("cases.generic_error")}
        />
        <SummarySectionModal
          isOpen={summaryModalVisible}
          onClose={setSummaryModalVisible}
          setSummaryModalVisible={setSummaryModalVisible}
          editingLayout={summaryEditingLayout}
          fields={summaryInitialValues}
        />

        <Container>
          <Row>
            <Col md={{ span: 12, offset: 0 }}>
              {formDetails.stepper === CaseStepperType.Full && (
                <CaseStepper layout={layout} current={activeStep} />
              )}
              {formDetails.stepper === CaseStepperType.Simple && (
                <CaseStepperSimple layout={layout} current={activeStep} />
              )}
            </Col>
          </Row>

          <Row>
            <Col className="below-text" xs={12} md={{ span: 10, offset: 1 }}>
              {aboveImg}
            </Col>
          </Row>

          <Formik
            initialValues={{
              ...initialValues,
              ...propsInitialValues,
            }}
            validationSchema={currentValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, errors, dirty, values }) => {
              dirtyFlag = dirty;
              return (
                <Form id={formId} className="case-generated-form" noValidate>
                  {/*<div>{JSON.stringify(errors)}</div>*/}
                  {incompleteActivation ? (
                    <RouteLeavingGuard
                      values={values}
                      when={!isSubmitting}
                      navigate={(path) => history.push(path)}
                      shouldBlockNavigation={() => {
                        return deactiveIncompleteControl ? false : dirty;
                      }}
                      mutation={incompleteMutation}
                    />
                  ) : null}
                  <Row>
                    <Col xs={12} md={{ span: 10, offset: 1 }}>
                      {isSummary ? (
                        <CaseSummary
                          fields={formField}
                          errors={errors}
                          layout={summaryLayout ? summaryLayout : layout}
                          setSummaryModalVisible={setSummaryModalVisible}
                          dataToUpdate={summaryDataToUpdate}
                          setDataToUpdate={setSummaryDataToUpdate}
                          setSummaryEditingLayout={(
                            layout: any,
                            values: any,
                            index: any,
                            absId: any
                          ) => {
                            setSummaryEditingLayout(
                              summaryLayout ? summaryLayout : layout
                            );
                            setSummaryInitialValues(values);
                            setSummaryValidationSchema(validationSchema[absId]);
                          }}
                          setActiveStep={setActiveStep}
                          setIsSummary={setIsSummary}
                        />
                      ) : (
                        <CaseStep
                          fields={formField}
                          errors={errors}
                          layout={currentLayout}
                          activeStep={activeStep}
                          handleButtonMatricolaContatoreCaseClick={
                            model.handleButtonMatricolaContatoreCaseClick
                          }
                          buttonVolturaIsClick={buttonVolturaIsClick}
                          validationSchema={currentValidationSchema}
                        />
                      )}
                      <div
                        className={
                          model.isVolturaSubentroCase && activeStep === 0
                            ? "action-buttons action-matricola"
                            : "action-buttons"
                        }
                      >
                        {activeStep === 0 && props.handleBack && (
                          <button
                            onClick={props.handleBack}
                            className="btn btn-transparent btn-icon back-button"
                            title={t("cases.buttons.back")}
                          >
                            <i
                              className="gc-icon-functional-arrow-back"
                              aria-hidden="true"
                            />
                            {t("cases.buttons.back")}
                          </button>
                        )}
                        {model.isVolturaSubentroCase && activeStep === 0 && (
                          <Button
                            className="gcap-cta"
                            onClick={() => {
                              setButtonVolturaIsClick(!buttonVolturaIsClick);
                            }}
                          >
                            <span className="gc-icon-functional-attention-2-col">
                              <span className="path1"></span>
                              <span className="path2"></span>
                            </span>

                            <p>{handleTextButton(buttonVolturaIsClick)}</p>
                            <i
                              className="gc-icon-functional-arrow-right"
                              aria-hidden="true"
                            ></i>
                          </Button>
                        )}
                        {activeStep !== 0 || isSummary ? (
                          <Button
                            onClick={handleBack}
                            className="gcap-cta"
                            variant=""
                          >
                            <i
                              className="gc-icon-functional-arrow-back"
                              aria-hidden="true"
                            />
                            {t("cases.buttons.back")}
                          </Button>
                        ) : (
                          <div>&nbsp;</div>
                        )}
                        <Button
                          disabled={isSubmitting}
                          type="submit"
                          // variant="contained"
                          color="primary"
                        >
                          <LabelCtaAction />
                        </Button>
                        <Modal
                          show={show}
                          onClose={() => {
                            setShow(false);
                          }}
                          description={t("voltura.enabled.modal")}
                        />
                        {isSubmitting && <Loader />}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col
                      className="below-text"
                      xs={12}
                      md={{ span: 10, offset: 1 }}
                    >
                      {belowText}
                    </Col>
                  </Row>
                </Form>
              );
            }}
          </Formik>
        </Container>
      </div>
    </CaseErrorBoundary>
  );
};


export default withTranslation()(CaseGenerator);
